/**
 * Intake API client.
 *
 * Two flavours:
 *  - `publicAxios` for the unauthenticated intake form (no Bearer token,
 *    no 401 interceptor — it must work for logged-out visitors).
 *  - `api` for the clerk-facing inbox endpoints, which re-uses the
 *    authenticated client so tokens and refresh are handled automatically.
 */
import axios from 'axios';
import { api } from './api';

const API_URL = (import.meta.env.VITE_API_URL ?? '').replace(/\/+$/, '');

const publicAxios = axios.create({
  baseURL: `${API_URL}/api/v1`,
  headers: { 'Content-Type': 'application/json' },
});

// ─── Shared types ───────────────────────────────────────────

export interface PublicChambersInfo {
  id: string;
  name: string;
  slug: string;
  logoUrl: string | null;
  primaryColor: string | null;
}

export interface IntakeSubmitPayload {
  solicitorFirmName: string;
  solicitorName: string;
  solicitorEmail: string;
  solicitorPhone?: string;
  solicitorSraNumber?: string;
  layClientName: string;
  layClientType?: 'individual' | 'company';
  opposingPartyName?: string;
  opposingSolicitor?: string;
  practiceArea?: string;
  caseType?: string;
  courtName?: string;
  hearingDate?: string;
  summary: string;
  urgency?: 'routine' | 'expedited' | 'urgent';
  feeExpectation?: string;
}

export interface IntakeSubmitResult {
  id: string;
  chambersName: string;
  receivedAt: string;
}

export type IntakeStatus =
  | 'new' | 'reviewing' | 'assessing' | 'quoted'
  | 'accepted' | 'rejected' | 'lapsed' | 'converted';
export type IntakeUrgency = 'routine' | 'expedited' | 'urgent';

/** How the work reached chambers. */
export type IntakeArrivalChannel =
  | 'phone' | 'email' | 'web_form' | 'portal' | 'direct' | 'referral' | 'internal';

/** Who is instructing — decides whether solicitor identity is required. */
export type IntakeInstructingPartyType =
  | 'professional_client' | 'lay_client' | 'portal' | 'internal';

export type IntakeFeeStatus = 'none' | 'estimated' | 'quoted' | 'agreed' | 'declined';

export type IntakeFeeBasis =
  | 'brief_fee' | 'refresher' | 'hourly' | 'fixed'
  | 'conference' | 'opinion' | 'cfa' | 'dba' | 'pro_bono';

export interface IntakeRequest {
  id: string;
  chambersId: string;
  arrivalChannel: IntakeArrivalChannel;
  instructingPartyType: IntakeInstructingPartyType;
  // NULLABLE: an enquiry from a lay client (Direct Access), a court portal, or raised
  // internally has no instructing solicitor. Always render through a fallback.
  solicitorFirmName: string | null;
  solicitorName: string | null;
  solicitorEmail: string | null;
  solicitorPhone: string | null;
  solicitorSraNumber: string | null;
  layClientName: string;
  layClientType: string;
  opposingPartyName: string | null;
  opposingSolicitor: string | null;
  practiceArea: string | null;
  caseType: string | null;
  courtName: string | null;
  hearingDate: string | null;
  summary: string;
  urgency: IntakeUrgency;
  feeExpectation: string | null;
  quotedAmount: string | number | null;
  feeBasis: IntakeFeeBasis | null;
  feeStatus: IntakeFeeStatus;
  preferredBarristerId: string | null;
  assignedToUserId: string | null;
  sourceEmailId: string | null;
  /**
   * Provisional diary holds against this enquiry. Returned on the DETAIL fetch only
   * (`fetchIntake`), never on the list — hence optional.
   */
  calendarEvents?: EnquiryHold[];
  status: IntakeStatus;
  reviewerId: string | null;
  reviewNotes: string | null;
  reviewedAt: string | null;
  convertedMatterId: string | null;
  rejectionReason: string | null;
  conflictStatus: 'clear' | 'potential' | 'critical' | null;
  conflictScannedAt: string | null;
  conflictDetails: IntakeConflictDetail[] | null;
  createdAt: string;
  updatedAt: string;
  chambers?: { id: string; name: string; slug: string };
}

export interface IntakeConflictDetail {
  severity: 'critical' | 'warning' | 'info';
  matchMethod: string;
  existingMatterId: string;
  existingMatterName: string;
  existingMatterNumber: string;
  existingBarrister: string | null;
  reason: string;
}

export interface IntakeStatusCounts {
  new: number;
  reviewing: number;
  assessing: number;
  quoted: number;
  accepted: number;
  rejected: number;
  lapsed: number;
  converted: number;
  total: number;
}

export interface IntakeListResponse {
  requests: IntakeRequest[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
  statusCounts: IntakeStatusCounts;
}

// ─── Public (no auth) ───────────────────────────────────────

export async function fetchPublicChambersInfo(slug: string): Promise<PublicChambersInfo> {
  const res = await publicAxios.get(`/intake/public/${slug}`);
  return res.data?.data ?? res.data;
}

export async function submitIntake(
  slug: string,
  payload: IntakeSubmitPayload,
): Promise<IntakeSubmitResult> {
  const res = await publicAxios.post(`/intake/public/${slug}`, payload);
  return res.data?.data ?? res.data;
}

// ─── Authenticated (clerk/admin inbox) ──────────────────────

export async function fetchIntakes(status?: IntakeStatus): Promise<IntakeListResponse> {
  const res = await api.get('/intake', { params: status ? { status } : {} });
  return res.data?.data ?? res.data;
}

/** What chambers staff supply when logging an enquiry they have taken. */
export interface LogEnquiryPayload {
  arrivalChannel?: IntakeArrivalChannel;
  instructingPartyType?: IntakeInstructingPartyType;
  solicitorFirmName?: string;
  solicitorName?: string;
  solicitorEmail?: string;
  solicitorPhone?: string;
  solicitorSraNumber?: string;
  layClientName: string;
  layClientType?: 'individual' | 'company';
  opposingPartyName?: string;
  opposingSolicitor?: string;
  practiceArea?: string;
  caseType?: string;
  courtName?: string;
  hearingDate?: string;
  summary: string;
  urgency?: IntakeUrgency;
  feeExpectation?: string;
  quotedAmount?: number;
  feeBasis?: IntakeFeeBasis;
  feeStatus?: IntakeFeeStatus;
  preferredBarristerId?: string;
  assignedToUserId?: string;
  status?: 'new' | 'reviewing' | 'assessing' | 'quoted';
}

/**
 * Log an enquiry chambers has taken — the phone call, the approach passed on by counsel,
 * portal access granted. Runs the same conflict scan as the public form.
 *
 * Note the status values accepted: an enquiry can be logged already under review, being
 * assessed or quoted, but never as `accepted` — acceptance is the regulated gate where the
 * conflict re-scan happens, and it cannot be asserted at creation.
 */
export async function logEnquiry(payload: LogEnquiryPayload): Promise<IntakeRequest> {
  const res = await api.post('/intake', payload);
  return res.data?.data ?? res.data;
}

/** A provisional diary hold placed against an enquiry — the clerk's pencil. */
export interface EnquiryHold {
  id: string;
  title: string;
  startAt: string;
  endAt: string;
  status: string;
  ownerId: string;
}

/**
 * Place a provisional hold on the diary for an enquiry.
 *
 * Written as `tentative`, so it occupies the diary and shows in free/busy — counsel will not be
 * double-booked — while committing chambers to nothing. It is deliberately NOT pushed to the
 * barrister's external Google/Outlook calendar; a pencil that reads as a booking to everyone
 * sharing that calendar is worse than no entry at all. On conversion the hold is confirmed and
 * re-pointed at the matter, inside the same transaction that creates it.
 */
export async function holdDateForEnquiry(payload: {
  intakeId: string;
  title: string;
  startAt: string;
  endAt: string;
  forUserId?: string;
}): Promise<EnquiryHold> {
  const res = await api.post('/calendar', {
    title: payload.title,
    startAt: payload.startAt,
    endAt: payload.endAt,
    status: 'tentative',
    eventType: 'other',
    intakeId: payload.intakeId,
    ...(payload.forUserId && { forUserId: payload.forUserId }),
  });
  return res.data?.data ?? res.data;
}

/** Release a provisional hold — the enquiry went away, so the diary must be freed. */
export async function releaseHold(eventId: string): Promise<void> {
  await api.delete(`/calendar/${eventId}`);
}

export async function fetchIntake(id: string): Promise<IntakeRequest> {
  const res = await api.get(`/intake/${id}`);
  return res.data?.data ?? res.data;
}

export async function updateIntake(
  id: string,
  patch: {
    status?: IntakeStatus;
    reviewNotes?: string;
    rejectionReason?: string;
    convertedMatterId?: string;
    conflictOverrideReason?: string;
  },
): Promise<IntakeRequest> {
  const res = await api.patch(`/intake/${id}`, patch);
  return res.data?.data ?? res.data;
}

/** Minimal shape of the matter returned by the atomic convert endpoint. */
export interface ConvertedMatter {
  id: string;
  caseNumber: string;
  caseName: string;
}

/**
 * Turn an accepted instruction into a live, operational matter in one atomic
 * action (server-side): links the solicitor contact, carries opponent/court/
 * practice-area/priority/fee, seeds any listed hearing into the diary, starts
 * the chronology at the instruction, and links intake↔matter in a single
 * transaction (no orphan matter / double-convert). Replaces the fragile old
 * two-call create-then-mark-converted path.
 */
export async function convertIntake(
  id: string,
  payload: {
    leadBarristerId?: string;
    caseName?: string;
    conflictOverrideReason?: string;
  } = {},
): Promise<ConvertedMatter> {
  const res = await api.post(`/intake/${id}/convert`, payload);
  return res.data?.data ?? res.data;
}
